import type { ITimelineItem, ITimelineRequestBody } from '~/types/types';
import moment, { type MomentInput } from 'moment';
import { storeToRefs } from 'pinia';
import { useAccountStore } from '~/stores/accountStore';
import { ETimelineFilterType, ETimelineIncludeType } from '~/types/enums';
import { computed, onBeforeUnmount, ref, watch, type WatchStopHandle } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useEventCalendarStore } from '~/stores/eventCalendar';

const EVENT_CALENDAR_PER_PAGE_LIMIT = 10;
const EVENT_CALENDAR_ORDER = ['validTo'];

const baseRequestBody: ITimelineRequestBody = {
    filter: {
        types: [ETimelineFilterType.CALENDAR_EVENT],
    },
    include: [ETimelineIncludeType.MR_CITY, ETimelineIncludeType.FILES, ETimelineIncludeType.IMAGES],
    order: EVENT_CALENDAR_ORDER,
    page: 1,
    perPage: EVENT_CALENDAR_PER_PAGE_LIMIT,
    includeExcludedTimelineGroupPosts: true,
};

export const useEventCalendarList = () => {
    const stopWatcher = ref<WatchStopHandle | null>(null);
    const isScrollable = ref(false);
    const page = ref(1);
    const abortController = ref(new AbortController());

    const { query } = useRoute();
    const { push } = useRouter();
    const { state } =  storeToRefs(useAccountStore());
    const eventCalendarStore = useEventCalendarStore();
    const { fetchEventCalendar: createCancelledEventCalendar } = eventCalendarStore;
    const { items, isLoading, validFrom, validTo, postGroupIds } = storeToRefs(eventCalendarStore);

    const requestBody = computed(() => {
        const _requestBody = { ...baseRequestBody, page: page.value };
        _requestBody.filter.cityId = [state.value.appCityId];

        if (validFrom.value && validTo.value) {
            _requestBody.filter.validInRange = { from: validFrom.value, to: validTo.value };
        }

        if (postGroupIds.value.length) {
            _requestBody.filter.groupIds = postGroupIds.value;
        } else {
            delete _requestBody.filter.groupIds;
        }

        return _requestBody;
    });

    const handleDataChange = (data?: ITimelineItem[]) => {
        if (!data?.length) return;
        isScrollable.value = !(data.length < EVENT_CALENDAR_PER_PAGE_LIMIT);
        page.value++;
    };

    const fetchEventCalendar = async () => {
        page.value = 1;
        items.value = [];
        items.value = await createCancelledEventCalendar(state.value.locale, requestBody.value);
        handleDataChange(items.value);
    };

    const onScroll = async () => {
        const data = await createCancelledEventCalendar(state.value.locale, requestBody.value);
        items.value = [...items.value, ...data];
        handleDataChange(data);
    };

    const stopParamsWatch = () => {
        stopWatcher.value?.();
    };

    const initParamsWatch = () => {
        const initWatch =  (): WatchStopHandle => watch([validFrom, validTo],  async () => {
           await fetchEventCalendar();
           const newQuery = {
               ...query,
               validFrom: moment(validFrom.value).format('YYYY-MM-DD'),
               validTo: moment(validTo.value).format('YYYY-MM-DD'),
           };
           await push({ query: newQuery });
        });
        stopWatcher.value = initWatch();
    };

    const setRange = (from: MomentInput, to: MomentInput) => {
        validFrom.value = moment(from).format('YYYY-MM-DD 00:00:00');
        validTo.value = moment(to).format('YYYY-MM-DD 23:59:59');
    };

    const isValidRange = (from: number | typeof NaN, to: number | typeof NaN) => {
        return !isNaN(from) && !isNaN(to) && from <= to;
    };

    const initRange = () => {
        const validFromParam = typeof query.validFrom === 'string' ? new Date(query.validFrom).getTime() : NaN;
        const validToParam = typeof query.validTo === 'string' ? new Date(query.validTo).getTime() : NaN;
        if (!isValidRange(validFromParam, validToParam)) {
            return setRange(moment().startOf('month'), moment().endOf('month'));
        }
        setRange(validFromParam, validToParam);
    };

    onBeforeUnmount(() => {
        abortController.value.abort();
        stopParamsWatch();
    });

    initRange();
    initParamsWatch();

    return { items, validFrom, validTo, isLoading, isScrollable, fetchEventCalendar, onScroll };
};
